import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Book, X } from 'lucide-react';
import api from '../api/axios';
import CustomSelectDropdown from "./CustomSelectDropdown";

const LedgerModal = ({ isOpen, onClose, onSuccess, editingLedger = null }) => {
const [formData, setFormData] = useState({
name: '',
description: ''
});

useEffect(() => {
    if (editingLedger) {
        setFormData({
            name: editingLedger.name,
            description: editingLedger.description || '',
            category: editingLedger.category || '',
            status: editingLedger.status || 'active',
        });
    } else {
        setFormData({
            name: '',
            description: '',
            category: '',
            status: 'active'
        });
    }
}, [editingLedger, isOpen]);

const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
};

const handleSubmit = async (e) => {
    e.preventDefault();
    try {
        if (editingLedger) {
            await api.put(`/ledgers/${editingLedger._id}`, formData);
        } else {
            await api.post('/ledgers', formData);
        }
        onSuccess();
    } catch (error) {
        console.error('Error saving ledger:', error);
    }
};

return (
    <AnimatePresence>
        {isOpen && (
            <>
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={onClose}
                    className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
                />

                <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: 20 }}
                    className="fixed inset-0 z-50 flex items-center justify-center p-4"
                >
                    <div className="w-full px-4 sm:px-6 flex justify-center items-center">
                        <div className="bg-white rounded-3xl shadow-2xl w-full max-w-3xl"> 
                            {/* Header */}
                            <div className="bg-indigo-600 px-8 py-6 flex items-center justify-between rounded-t-3xl">
                                <div className="flex items-center gap-3 text-white">
                                    <Book size={32} className="hidden sm:block" />
                                    <div>
                                        <h2 className="text-2xl font-bold">
                                            {editingLedger ? "Edit Book" : "Create New Book"}
                                        </h2>
                                        <p className="text-indigo-100 text-sm mt-1">
                                            {editingLedger ? "Update book details" : "Add a new expense book"}
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={onClose}
                                    className="p-2 hover:bg-white/20 rounded-full transition-colors"
                                >
                                    <X size={26} className="text-white" />
                                </button>
                            </div>

                            {/* Form */}
                            <form onSubmit={handleSubmit} className="p-8">
                                
                                {/* 2 Column Grid Layout */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    
                                    {/* Book Name */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Book Name *
                                        </label>
                                        <input
                                            type="text"
                                            required
                                            value={formData.name}
                                            onChange={(e) => handleChange("name", e.target.value)}
                                            placeholder="e.g., Household Expense"
                                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500"
                                        />
                                    </div>

                                    {/* Category Dropdown */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Category *
                                        </label>
                                        <CustomSelectDropdown
                                            label=""
                                            value={formData.category}
                                            onChange={(v) => handleChange("category", v)}
                                            options={[
                                                "Personal",
                                                "Business",
                                                "Household",
                                                "Travel",
                                                "Project",
                                                "General",
                                            ]}
                                            placeholder="Select a category"
                                        />
                                    </div>

                                    {/* Description */}
                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Description (Optional)
                                        </label>
                                        <textarea
                                            value={formData.description}
                                            onChange={(e) => handleChange("description", e.target.value)}
                                            placeholder="Add a brief description..."
                                            rows={3}
                                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 resize-none"
                                        />
                                    </div>

                                    {/* Status Dropdown */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Status *
                                        </label>
                                        <CustomSelectDropdown
                                            label=""
                                            value={formData.status}
                                            onChange={(v) => handleChange("status", v)}
                                            options={["active", "archived", "draft"]}
                                            placeholder="Select status"
                                        />
                                    </div>
                                </div>

                                {/* Suggestions */}
                                <div className="mt-8">
                                    <p className="text-sm font-medium text-gray-700 mb-3">Quick Suggestions</p>
                                    <div className="flex flex-wrap gap-2">
                                        {["Household", "Business", "Personal", "Travel", "Farming", "Project"].map(
                                            (suggestion) => (
                                                <button
                                                    key={suggestion}
                                                    type="button"
                                                    onClick={() => handleChange("name", `${suggestion} Expense`)}
                                                    className="px-4 py-2 bg-indigo-50 text-indigo-600 rounded-lg 
                                                    text-sm font-medium hover:bg-indigo-100"
                                                >
                                                    {suggestion}
                                                </button>
                                            )
                                        )}
                                    </div>
                                </div>

                                {/* Buttons */}
                                <div className="flex flex-col sm:flex-row gap-3 pt-8">
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        className="flex-1 px-6 py-3 border border-gray-300 rounded-xl 
                                        text-gray-700 hover:bg-gray-50"
                                    >
                                        Cancel
                                    </button>

                                    <button
                                        type="submit"
                                        className="flex-1 px-6 py-3 bg-indigo-600 hover:bg-indigo-700 
                                        text-white rounded-xl font-medium shadow-md hover:shadow-lg transition-all"
                                    >
                                        {editingLedger ? "Update" : "Create"} Book
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </motion.div>
            </>
        )}
    </AnimatePresence>
);

};

export default LedgerModal;
